// ---------------------------------------------------------------------------
// Mood Themes
// ---------------------------------------------------------------------------
// Shared page-level mood presets for editor preview and published rendering.

export type MoodId = "minimal" | "bold" | "elegant" | "playful" | "dark";

export interface MoodThemeTokens {
  readonly primary: string;
  readonly primaryHover: string;
  readonly accent: string;
  readonly background: string;
  readonly surface: string;
  readonly foreground: string;
  readonly muted: string;
  readonly border: string;
}

export interface MoodTheme {
  readonly id: MoodId;
  readonly name: string;
  readonly description: string;
  readonly previewColor: string;
  readonly previewAccent: string;
  readonly tokens: MoodThemeTokens;
}

const MINIMAL: MoodTheme = {
  id: "minimal",
  name: "Minimal",
  description: "Clean, understated, focused",
  previewColor: "#FFFFFF",
  previewAccent: "#E5E7EB",
  tokens: {
    primary: "#111827",
    primaryHover: "#0F172A",
    accent: "#6B7280",
    background: "#FFFFFF",
    surface: "#F8FAFC",
    foreground: "#111827",
    muted: "#E5E7EB",
    border: "#E5E7EB",
  },
};

const BOLD: MoodTheme = {
  id: "bold",
  name: "Bold",
  description: "High contrast, impactful",
  previewColor: "#111827",
  previewAccent: "#F05E0A",
  tokens: {
    primary: "#F05E0A",
    primaryHover: "#C2410C",
    accent: "#FBBF24",
    background: "#111827",
    surface: "#1F2937",
    foreground: "#FFFFFF",
    muted: "#374151",
    border: "#374151",
  },
};

const ELEGANT: MoodTheme = {
  id: "elegant",
  name: "Elegant",
  description: "Refined, sophisticated, airy",
  previewColor: "#FAFAF8",
  previewAccent: "#C4B5FD",
  tokens: {
    primary: "#7C3AED",
    primaryHover: "#6D28D9",
    accent: "#C4B5FD",
    background: "#FAFAF8",
    surface: "#F3F0EA",
    foreground: "#1F2937",
    muted: "#D6D3D1",
    border: "#D6D3D1",
  },
};

const PLAYFUL: MoodTheme = {
  id: "playful",
  name: "Playful",
  description: "Colorful, rounded, energetic",
  previewColor: "#EEF3FF",
  previewAccent: "#7C3AED",
  tokens: {
    primary: "#2563EB",
    primaryHover: "#1D4ED8",
    accent: "#7C3AED",
    background: "#EEF3FF",
    surface: "#FFFFFF",
    foreground: "#111827",
    muted: "#C7D2FE",
    border: "#BDD3FF",
  },
};

const DARK: MoodTheme = {
  id: "dark",
  name: "Dark",
  description: "Near-black, subtle glow, sleek",
  previewColor: "#0F1117",
  previewAccent: "#1040CC",
  tokens: {
    primary: "#1040CC",
    primaryHover: "#0B2F99",
    accent: "#38BDF8",
    background: "#0F1117",
    surface: "#171A23",
    foreground: "#F8F9FC",
    muted: "#2D3141",
    border: "#2D3141",
  },
};

export const MOOD_THEMES: readonly MoodTheme[] = [
  MINIMAL,
  BOLD,
  ELEGANT,
  PLAYFUL,
  DARK,
] as const;

export const MOOD_THEMES_MAP: Readonly<Record<MoodId, MoodTheme>> = {
  minimal: MINIMAL,
  bold: BOLD,
  elegant: ELEGANT,
  playful: PLAYFUL,
  dark: DARK,
} as const;

export function isMoodId(value: unknown): value is MoodId {
  return typeof value === "string" && value in MOOD_THEMES_MAP;
}

export function getMoodTheme(moodId: MoodId | null | undefined): MoodTheme | null {
  if (!moodId) return null;
  return MOOD_THEMES_MAP[moodId] ?? null;
}

export function buildEditorMoodVariables(moodId: MoodId): Record<string, string> {
  const theme = MOOD_THEMES_MAP[moodId];

  return {
    "--color-primary": theme.tokens.primary,
    "--color-primary-hover": theme.tokens.primaryHover,
    "--color-primary-light": theme.tokens.surface,
    "--color-primary-border": theme.tokens.border,
    "--color-secondary": theme.tokens.primaryHover,
    "--color-accent": theme.tokens.accent,
    "--color-accent-action": theme.tokens.accent,
    "--color-background": theme.tokens.background,
    "--color-surface": theme.tokens.surface,
    "--color-foreground": theme.tokens.foreground,
    "--color-muted": theme.tokens.muted,
    "--color-muted-foreground": theme.tokens.primaryHover,
    "--color-border": theme.tokens.border,
    "--color-card": theme.tokens.surface,
    "--color-card-foreground": theme.tokens.foreground,
    "--color-input": theme.tokens.border,
    "--color-ring": theme.tokens.primary,
    "--gradient-energy": `linear-gradient(135deg, ${theme.tokens.primary} 0%, ${theme.tokens.accent} 100%)`,
    backgroundColor: theme.tokens.background,
    color: theme.tokens.foreground,
  };
}

export function buildPublishedMoodVariables(moodId: MoodId): Record<string, string> {
  const theme = MOOD_THEMES_MAP[moodId];

  return {
    "--ease-color-brand": theme.tokens.primary,
    "--ease-color-brand-dark": theme.tokens.primaryHover,
    "--ease-color-secondary": theme.tokens.primaryHover,
    "--ease-color-accent": theme.tokens.accent,
    "--ease-color-bg": theme.tokens.background,
    "--ease-color-surface": theme.tokens.surface,
    "--ease-color-text": theme.tokens.foreground,
    "--ease-color-muted": theme.tokens.muted,
    "--ease-color-border": theme.tokens.border,
    "--ease-color-text-soft": theme.tokens.primaryHover,
    "--ease-color-surface-muted": theme.tokens.surface,
    "--ease-button-primary-bg": theme.tokens.primary,
    "--ease-button-primary-text": "#FFFFFF",
    "--ease-button-secondary-bg": theme.tokens.surface,
    "--ease-button-secondary-text": theme.tokens.foreground,
    "--ease-card-border": theme.tokens.border,
  };
}
